import { FormTypes } from 'constants/formTypes';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router';
import { connect } from 'react-redux';
import i18n from 'util/i18n';
import StickyHeader from 'components/Sticky/StickyHeader';
import Introduction from 'components/Form/Introduction';
import { ProgressBar } from 'components/ProgressBar';
import { AppKeydownWatchers } from 'util/singletons';
import { renewSessionRequest } from '@redux/modules/authentication/actions';
import FormChoices from 'components/Form/FormChoices';
import { RenewSessionRequestAction } from 'redux/modules/authentication/actions';
import { RootState } from 'types/state';
import applicationReducer, { ApplicationSettingsState } from '@redux/modules/application/reducer';
import OfficialWebsite from 'components/OfficialWebsite';
import HeaderBanner from 'components/HeaderBanner';
import { APIState } from '@redux/modules/api/reducer';
import {
  FetchIdentityStatusData,
  FetchIdentityStatusRequestAction,
  FetchSetAccountRequestAction,
  IdentityStatusAccount,
  fetchIdentityStatusRequest,
  fetchSetAccountRequest,
} from '@redux/modules/api/actions';
import { FormChoicesForm } from 'components/Form/FormChoices/FormChoices';
import { AuthenticationState } from '@redux/modules/authentication/reducer';
import TimeoutWarning from 'components/Form/TimeoutWarning';
import AnnouncementBannerList from 'components/AnnouncementBannerList/AnnouncementBannerList';

type LandingPageDispatchProps = {
  dispatchFetchIdentityStatusRequest: () => void;
  dispatchFetchSetAccountRequest: (requestId: string, status: string) => void;
  keepSessionAlive: () => void;
}

type LandingPageOwnProps = {
  // A dummy variable used to prevent a typing error with the call to connector(LandingPage)
  // For a possible fix, see https://react-redux.js.org/using-react-redux/usage-with-typescript
  connectorTypingDummy: boolean;
}

type LandingPageStateProps = {
  apiVersionText: string;
  clientVersionText: string;
  formChoicesForms: FormChoicesForm[];
  formLoaded: boolean;
  mobileNavigation: boolean;
  showSessionWarning: boolean;
setLockedForm: boolean;
}

type LandingPageProps = LandingPageDispatchProps & LandingPageOwnProps & LandingPageStateProps;

class LandingPage extends Component<LandingPageProps> {
  /** Tick every minute */
  private static readonly REFRESH_INTERVAL = 60_000;

  // TODO: Might need to update the eslint configuration:
  // https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/static-property-placement.md
  // eslint-disable-next-line react/static-property-placement
  static readonly defaultProps: LandingPageOwnProps = {
    connectorTypingDummy: false,
  };

  // eslint-disable-next-line react/static-property-placement
  static readonly propTypes = {
    apiVersionText: PropTypes.string.isRequired,
    clientVersionText: PropTypes.string.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    connectorTypingDummy: PropTypes.bool,
    dispatchFetchIdentityStatusRequest: PropTypes.func.isRequired,
    dispatchFetchSetAccountRequest: PropTypes.func.isRequired,
    formChoicesForms: PropTypes.array.isRequired,
    formLoaded: PropTypes.bool.isRequired,
    keepSessionAlive: PropTypes.func.isRequired,
    mobileNavigation: PropTypes.bool.isRequired,
    showSessionWarning: PropTypes.bool.isRequired,
  };

  private refreshTimerId = 0;

  componentDidMount(): void {
    this.props.dispatchFetchIdentityStatusRequest();

    const appKeydownWatchers: AppKeydownWatchers = AppKeydownWatchers.getInstance();

    appKeydownWatchers.watchErrorDebugger(true);

    appKeydownWatchers.watchUserActivity(true);

    this.startRefreshTimer();
  }

  componentWillUnmount(): void {
    const appKeydownWatchers: AppKeydownWatchers = AppKeydownWatchers.getInstance();

    appKeydownWatchers.watchErrorDebugger(false);

    appKeydownWatchers.watchUserActivity(false);

    window.clearInterval(this.refreshTimerId);
  }

  private onFormSelection = (formChoicesForm: FormChoicesForm) => {
    this.props.dispatchFetchSetAccountRequest(formChoicesForm.externalId, formChoicesForm.status);
  };

  /**
  This timer will run every minute for the duration of the user using the application.
  In addition refreshing will occur when the user saves.
  In any given minute where user activity occurs (typing or clicking) the session will be refreshed.
  This refresh action will occur within one minute of the user's manual action
  If the session timeout is nearly up the modal will pop up to have the user manually push the button.
  These session refreshes are separate from save events which occur regularly and do not require a
  session refresh upon each action.

  This particular model will not allow the API to have more than SessionTimeout between client interactions;
  however, some client interaction may be deferred by up to a minute. This spares client and server processing by
  using each minute to batch refreshes resulting in clients having no more than one refresh per minute. This
  monitors mouse click interaction and keyboard button presses.

  The purpose of this is a person who is activly typing or clicking in any section, reviewing, inputting or
  otherwise interacting should not be interrupted with timeout notices. A user who remains idle and waits until
  the final minute before the timeout is displayed to interact with the screen may still be presented with the continue
  session timeout modal, but should have all data auto saved.
  If the user's last action was within the final minute of the timeout.
  This will fully prevent an active user who is using the system encountering an unexpected timeout.
  */
  private startRefreshTimer(): void {
    this.refreshTimerId = window.setInterval(this.props.keepSessionAlive, LandingPage.REFRESH_INTERVAL);
  }

  render() {
    const {
      apiVersionText,
      clientVersionText,
      mobileNavigation,
      showSessionWarning,
    } = this.props;

    if (this.props.setLockedForm) {
      return (<Redirect to={{ pathname: '/locked' }} />);
    }

    if (this.props.formLoaded) {
      return (<Redirect to="/form/identification/intro" />);
    }

    const forms: FormChoicesForm[] = this.props.formChoicesForms || [];

    return (
      <div className="landing-page">
        <Introduction />
        <div id="scrollTo" />
        <a className="usa-skipnav" href="#main-content">
          {i18n.t('app.skip')}
        </a>
        <StickyHeader stickyClass="sticky-header">
          <div className="header-container">
            <div className="header">
              <header className="usa-header usa-header-basic" role="banner">
                <OfficialWebsite />
                <HeaderBanner
                  mobileNavigation={mobileNavigation}
                  showSurvey={false}
                  submitted={false}
                />
              </header>
              <div id="scrollToProgress" />
              <div className="usa-overlay" />
              <ProgressBar />
            </div>
          </div>
        </StickyHeader>
        <AnnouncementBannerList />
        <FormChoices
          forms={forms}
          onFormSelection={this.onFormSelection}
        />
        <div
          style={{
            position: 'absolute', top: '0px', right: '0px', width: '20px', height: '40px'
          }}
          title={`Client Version: ${clientVersionText}\nAPI Version: ${apiVersionText}\n`}
        >
          &nbsp;
        </div>
        {showSessionWarning && (<TimeoutWarning />)}
      </div>
    );
  }
}

function toFormChoicesForm(identityStatusAccount: IdentityStatusAccount): FormChoicesForm {
  const {
    CreatedDate,
    ExternalId,
    FormType,
    FormVersion,
    OrgName,
    PocEmail,
    PocName,
    Status,
  } = identityStatusAccount;

  const createdDate: string = CreatedDate ? new Date(CreatedDate).toLocaleDateString('en-US') : '';

  const formChoicesForm: FormChoicesForm = {
    createdDate,
    externalId: ExternalId,
    formType: FormType as FormTypes,
    formVersion: FormVersion,
    orgName: OrgName,
    pocEmail: PocEmail,
    pocName: PocName,
    status: Status,
  };

  return formChoicesForm;
}

function mapDispatchToProps(
  dispatch: (action:
    | FetchIdentityStatusRequestAction
    | FetchSetAccountRequestAction
    | RenewSessionRequestAction) => void
): LandingPageProps {
  const props: LandingPageDispatchProps = {
    dispatchFetchIdentityStatusRequest: () => {
      dispatch(fetchIdentityStatusRequest());
    },
    dispatchFetchSetAccountRequest: (requestId: string, status: string) => {
      dispatch(fetchSetAccountRequest(requestId, status));
    },
    keepSessionAlive: () => {
      const watcher: AppKeydownWatchers = AppKeydownWatchers.getInstance();

      if (watcher.data.userActivity) {
        dispatch(renewSessionRequest(null));

        watcher.restartUserActivity();
      }
    },
  };

  return props as LandingPageProps;
}

function mapStateToProps(state: RootState): LandingPageStateProps {
  const api: APIState = state.api;

  const apiVersionText: string = api.apiVersion.apiVersionText || '';

  const clientVersionText: string = api.clientVersion.clientVersionText || '';

  const application: ReturnType<typeof applicationReducer> = state.application;

  const applicationSettings: ApplicationSettingsState = application.Settings;

  const mobileNavigation = Boolean(applicationSettings.mobileNavigation);

  const fetchIdentityStatusData: FetchIdentityStatusData | undefined = api.fetchIdentityStatus?.response as FetchIdentityStatusData;

  const identityStatusAccounts: IdentityStatusAccount[] = fetchIdentityStatusData?.accounts || [];

  const formChoicesForms: FormChoicesForm[] = identityStatusAccounts.map(toFormChoicesForm);

  const formLoaded = Boolean(api.fetchForm.formLoaded);

  const setLockedForm = !!api.setLockedForm;

  const authentication: AuthenticationState = state.authentication;

  const showSessionWarning = authentication.showSessionWarning;

  return {
    apiVersionText,
    clientVersionText,
    formChoicesForms,
    formLoaded,
    mobileNavigation,
    showSessionWarning,
    setLockedForm,
  };
}

export const internalTestingApp = {
  LandingPage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(connector(LandingPage));
